/**
 * Status-bar pickers — the original's size, grid-snap and layer popups
 * (OG3.cpp:1070-1285).
 *
 * The size picker is the interesting one. It does not offer a fixed list of
 * widths: it walks the document, collects every width already in use, sorts
 * them and radio-checks the current default. So laying a wider track means
 * clicking the size field and picking a width you have already used, and a
 * width only enters the list by being drawn once. That keeps a board's set of
 * track widths self-describing instead of arbitrary.
 *
 * Each popup ends with an escape hatch — "Select Line Width", "Edit Table",
 * "Modify Layer Table" — exactly as the original does.
 */

import type { CadDocument, Entity } from '@sircad/core/model.ts';
import {
  editValue, formatDisplay, fromDisplay, readEdit, unitName, type DisplayUnit,
} from '@sircad/core/units.ts';
import { layerTypeLabel } from '../render/renderer.ts';

export interface PickerItem {
  label: string;
  /** Radio-checked, as the original marks the current value. */
  checked?: boolean;
  /** Colour swatch, used by the layer picker. */
  swatch?: number;
  /** Secondary text, right-aligned. */
  note?: string;
  separator?: boolean;
  onPick?: () => void;
  /** Shown greyed, e.g. a layer whose display is off. */
  dimmed?: boolean;
  /** Right-click action, e.g. toggle a layer's visibility. */
  onRightClick?: () => void;
  /** Hover tooltip. */
  title?: string;
}

let openPopup: HTMLElement | null = null;

/** Close any open picker. */
export function closePicker(): void {
  openPopup?.remove();
  openPopup = null;
}

/** Show a picker anchored above a status-bar field. */
export function showPicker(anchor: HTMLElement, items: PickerItem[]): void {
  closePicker();

  const popup = document.createElement('div');
  popup.className = 'picker';

  for (const item of items) {
    if (item.separator) {
      popup.append(Object.assign(document.createElement('div'), { className: 'picker-sep' }));
      continue;
    }

    const btn = document.createElement('button');
    btn.className = 'picker-item'
      + (item.checked ? ' checked' : '')
      + (item.dimmed ? ' dimmed' : '');
    if (item.title) btn.title = item.title;

    const mark = document.createElement('span');
    mark.className = 'pi-mark';
    mark.textContent = item.checked ? '•' : '';

    const label = document.createElement('span');
    label.className = 'pi-label';
    if (item.swatch !== undefined) {
      const sw = document.createElement('span');
      sw.className = 'pi-swatch';
      sw.style.background = `#${(item.swatch >>> 0).toString(16).padStart(6, '0').slice(-6)}`;
      label.append(sw);
    }
    label.append(document.createTextNode(item.label));

    btn.append(mark, label);
    if (item.note) {
      const note = document.createElement('span');
      note.className = 'pi-note';
      note.textContent = item.note;
      btn.append(note);
    }

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      closePicker();
      item.onPick?.();
    });
    // Right-click toggles without dismissing, so several layers can be flipped
    // in a row (used to show/hide layers from the status-bar layer picker).
    if (item.onRightClick) {
      btn.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        item.onRightClick!();
      });
    }
    popup.append(btn);
  }

  document.body.append(popup);
  openPopup = popup;

  // Anchor above the status bar, clamped to the viewport.
  const rect = anchor.getBoundingClientRect();
  const pr = popup.getBoundingClientRect();
  popup.style.left = `${Math.max(4, Math.min(rect.left, window.innerWidth - pr.width - 4))}px`;
  popup.style.top = `${Math.max(4, rect.top - pr.height - 2)}px`;

  // A click elsewhere dismisses it, matching a tracked popup menu.
  setTimeout(() => {
    document.addEventListener('click', closePicker, { once: true });
  }, 0);
}

// ---------------------------------------------------------------------------
// Harvesting
// ---------------------------------------------------------------------------

/**
 * Every line or arc width used at the top level of a document.
 *
 * Top level only, deliberately: component children carry the library's silk
 * and pad geometry, and mixing those in would bury your own track widths under
 * dozens of footprint values.
 */
export function usedWidths(doc: CadDocument, kinds: Entity['kind'][]): number[] {
  const seen = new Set<number>();
  for (const e of doc.entities) {
    if (!kinds.includes(e.kind)) continue;
    const w = (e as { width?: number }).width;
    if (typeof w === 'number' && w > 0) seen.add(Number(w.toFixed(6)));
  }
  return [...seen].sort((a, b) => a - b);
}

/** Pad sizes in use, as `x,y` pairs. */
export function usedPadSizes(doc: CadDocument): { x: number; y: number }[] {
  const seen = new Map<string, { x: number; y: number }>();
  const visit = (list: Entity[]): void => {
    for (const e of list) {
      if (e.kind === 'component') { visit(e.children); continue; }
      if (e.kind !== 'pad') continue;
      const key = `${e.sizeX.toFixed(4)}x${e.sizeY.toFixed(4)}`;
      if (!seen.has(key)) seen.set(key, { x: e.sizeX, y: e.sizeY });
    }
  };
  visit(doc.entities);
  return [...seen.values()].sort((a, b) => a.x - b.x || a.y - b.y);
}

// ---------------------------------------------------------------------------
// The three pickers
// ---------------------------------------------------------------------------

export interface PickerContext {
  doc: CadDocument;
  units: DisplayUnit;
  /** Which primitive the active tool draws, so the size list matches it. */
  target: 'line' | 'arc' | 'pad' | 'via';
  onChanged(message: string): void;
  /** Open Setup > Layers. */
  openLayerSetup(): void;
  setActiveLayer(layer: number): void;
}

export function showSizePicker(anchor: HTMLElement, ctx: PickerContext): void {
  const { doc, units } = ctx;
  const items: PickerItem[] = [];

  if (ctx.target === 'pad' || ctx.target === 'via') {
    const current = ctx.target === 'via' ? doc.defaults.via : doc.defaults.pad;
    const sizes = usedPadSizes(doc);
    for (const s of sizes) {
      const same = Math.abs(s.x - (current?.size ?? 0)) < 1e-9;
      items.push({
        label: s.x === s.y
          ? formatDisplay(s.x, units)
          : `${formatDisplay(s.x, units)} × ${formatDisplay(s.y, units)}`,
        checked: same,
        onPick: () => {
          if (ctx.target === 'via' && doc.defaults.via) doc.defaults.via.size = s.x;
          else if (doc.defaults.pad) doc.defaults.pad.size = s.x;
          ctx.onChanged(`Pad size ${formatDisplay(s.x, units)}`);
        },
      });
    }
    items.push({ label: '', separator: true });
    items.push({
      label: `Select ${ctx.target === 'via' ? 'Via' : 'Pad'} Size`,
      onPick: () => {
        const v = askLength(`Pad size in ${unitName(units)}:`, current?.size ?? 0.06, units);
        if (v === null) return;
        if (ctx.target === 'via' && doc.defaults.via) doc.defaults.via.size = v;
        else if (doc.defaults.pad) doc.defaults.pad.size = v;
        ctx.onChanged(`Pad size ${formatDisplay(v, units)}`);
      },
    });
    showPicker(anchor, items);
    return;
  }

  // Line and arc share the default line width, as they do in the document.
  const current = doc.defaults.line?.width ?? 0.01;
  const widths = usedWidths(doc, ctx.target === 'arc' ? ['arc'] : ['line', 'fill']);

  // The current width belongs in the list even if nothing is drawn with it yet.
  if (!widths.some((w) => Math.abs(w - current) < 1e-9)) {
    widths.push(Number(current.toFixed(6)));
    widths.sort((a, b) => a - b);
  }

  for (const w of widths) {
    items.push({
      label: formatDisplay(w, units),
      checked: Math.abs(w - current) < 1e-9,
      onPick: () => {
        if (doc.defaults.line) doc.defaults.line.width = w;
        else doc.defaults.line = { width: w, style: 0 };
        ctx.onChanged(`Width ${formatDisplay(w, units)}`);
      },
    });
  }

  items.push({ label: '', separator: true });
  items.push({
    label: ctx.target === 'arc' ? 'Select Arc Width' : 'Select Line Width',
    onPick: () => {
      const v = askLength(`Track width in ${unitName(units)}:`, current, units);
      if (v === null) return;
      if (doc.defaults.line) doc.defaults.line.width = v;
      else doc.defaults.line = { width: v, style: 0 };
      ctx.onChanged(`Width ${formatDisplay(v, units)}`);
    },
  });

  showPicker(anchor, items);
}

export function showGridPicker(anchor: HTMLElement, ctx: PickerContext): void {
  const { doc, units } = ctx;
  const table = doc.grid.table.length > 0 ? doc.grid.table : [0.001, 0.005, 0.025, 0.05, 0.1];

  const items: PickerItem[] = table.map((g, i) => ({
    label: formatDisplay(g, units),
    checked: Math.abs(g - doc.grid.current) < 1e-9,
    onPick: () => {
      doc.grid.current = g;
      doc.grid.index = i;
      ctx.onChanged(`Grid ${formatDisplay(g, units)}`);
    },
  }));

  items.push({ label: '', separator: true });
  items.push({
    label: 'Edit Table',
    onPick: () => {
      const raw = window.prompt(
        `Grid steps in ${unitName(units)}, comma separated:`,
        table.map((g) => Number(formatDisplay(g, units).replace(/[^\d.-]/g, ''))).join(', '),
      );
      if (raw === null) return;
      const next = raw.split(',')
        .map((s) => parseFloat(s.trim()))
        .filter((v) => Number.isFinite(v) && v > 0)
        .map((v) => fromDisplay(v, units))
        .sort((a, b) => a - b);
      if (next.length === 0) return;
      doc.grid.table = next;
      if (!next.some((g) => Math.abs(g - doc.grid.current) < 1e-9)) {
        doc.grid.current = next[0]!;
        doc.grid.index = 0;
      }
      ctx.onChanged(`Grid table: ${next.length} steps`);
    },
  });

  showPicker(anchor, items);
}

export function showLayerPicker(
  anchor: HTMLElement, ctx: PickerContext, activeLayer: number,
): void {
  const items: PickerItem[] = ctx.doc.layers.map((l, i) => ({
    label: l.name,
    note: layerTypeLabel(l),
    swatch: l.screen,
    checked: i === activeLayer,
    dimmed: !l.visible,
    title: `${l.name} — click to make active, right-click to ${l.visible ? 'hide' : 'show'}`,
    onPick: () => {
      ctx.setActiveLayer(i);
      ctx.onChanged(`Layer: ${l.name}`);
    },
    // Right-click flips the layer's display and keeps the picker open so more
    // can be toggled; reopen it so the greying updates in place.
    onRightClick: () => {
      l.visible = !l.visible;
      ctx.onChanged(`${l.name} ${l.visible ? 'shown' : 'hidden'}`);
      showLayerPicker(anchor, ctx, activeLayer);
    },
  }));

  items.push({ label: '', separator: true });
  items.push({ label: 'Modify Layer Table', onPick: () => ctx.openLayerSetup() });

  showPicker(anchor, items);
}

/** Prompt for a length in the display unit, returning inches. */
function askLength(prompt: string, current: number, units: DisplayUnit): number | null {
  const shown = Number(formatDisplay(current, units).replace(/[^\d.-]/g, ''));
  const raw = window.prompt(prompt, String(shown));
  if (raw === null) return null;
  const v = parseFloat(raw.trim());
  if (!Number.isFinite(v) || v <= 0) return null;
  return fromDisplay(v, units);
}
